<template>
    <div>
        <Table
            :columns="columns"
            :data="resources"
            size="small"
            :show-summary="true"
            :summary-method="handleSummary"
            stripe
        >
            <template slot-scope="{ row }" slot="account_number">
                {{ row.account ? row.account.account_number : "" }}
            </template>
            <template slot-scope="{ row }" slot="account_name">
                {{ row.account ? row.account.account_name_en : "" }}
                {{
                    row.account.account_name_kh
                        ? "|" + row.account.account_name_kh
                        : ""
                }}
            </template>
            <template slot-scope="{ row }" slot="currency">
                {{ row.currency ? row.currency.currency_code : "" }}
            </template>
            <template slot-scope="{ row }" slot="debit_amount">
                {{ formatCurrencyWithCode(row.debit_amount, row.currency) }}
            </template>
            <template slot-scope="{ row }" slot="credit_amount">
                {{ formatCurrencyWithCode(row.credit_amount, row.currency) }}
            </template>
        </Table>
    </div>
</template>
<script>
import { mapGetters, mapState } from "vuex";
import { orderBy } from "lodash";
export default {
    props: {
        row: Object,
    },
    computed: {
        ...mapState("accounting/journalEntry", ["currencies"]),
        ...mapGetters(["formatCurrencyWithCode"]),
        resources() {
            return orderBy(this.row.details, ["display_order"]);
        },
        currency() {
            return this.row.currency;
        },
        columns() {
            return [
                {
                    title: this.$t("journalEntry.accountNumber"),
                    key: "account_number",
                    slot: "account_number",
                    sortable: true,
                    width: 200
                },
                {
                    title: this.$t("journalEntry.accountName"),
                    key: "account_name",
                    slot: "account_name",
                    sortable: true,
                    width: 450
                },
                {
                    title: this.$t("journalEntry.currency"),
                    slot: "currency",
                    sortable: true,
                    width: 120,
                    align: "center"
                },
                {
                    title: this.$t("journalEntry.fxRate"),
                    key: "fx_rate",
                    sortable: true,
                    width: 120,
                    align: "center"
                },
                {
                    title: this.$t("journalEntry.debitAmount"),
                    key: "debit_amount",
                    slot: "debit_amount",
                    sortable: true,
                    width: 200,
                    align: "right"
                },
                {
                    title: this.$t("journalEntry.creditAmount"),
                    key: "credit_amount",
                    slot: "credit_amount",
                    sortable: true,
                    width: 200,
                    align: "right"
                }
            ];
        }
    },
    methods: {
        handleSummary({ columns, data }) {
            const sums = {};
            const mainCurrency = this.currency.currency_id;
            const mainFxRate = Number(this.row.fx_rate) || 1;

            columns.forEach((column, index) => {
                const key = column.key;

                if (index === 0) {
                    sums[key] = {
                        key,
                        value: this.$t("journalEntry.total")
                    };
                    return;
                }

                if (index === 3) {
                    sums[key] = {
                        key,
                        value: "N/A"
                    };
                    return;
                }

                // Special handling for debit_amount & credit_amount
                if (key === "debit_amount" || key === "credit_amount") {
                    const total = data.reduce((prev, curr) => {
                        const amount = Number(curr[key]) || 0;
                        const lineCurrency = curr.currency_id;
                        const lineFxRate = Number(curr.fx_rate) || 1;

                        // If same currency, no conversion needed
                        if (lineCurrency === mainCurrency) {
                            return prev + amount;
                        }

                        if (mainCurrency === 2) {
                            // Main is KHR, line is USD
                            // Use line's fx_rate to convert USD to KHR
                            return prev + amount * lineFxRate;
                        } else if (mainCurrency === 1) {
                            // Main is USD, line is KHR
                            // Use main fx_rate to convert KHR to USD
                            return prev + amount / mainFxRate;
                        }

                        return prev + amount;
                    }, 0);

                    sums[key] = {
                        key,
                        value: this.formatCurrencyWithCode(total, this.currency)
                    };
                    return;
                }

                // Default handling for other numeric columns
                const values = data.map(item => Number(item[key]));
                if (!values.every(value => isNaN(value))) {
                    const v = values.reduce((prev, curr) => {
                        const value = Number(curr);
                        if (!isNaN(value)) {
                            return prev + value;
                        } else {
                            return prev;
                        }
                    }, 0);
                    sums[key] = {
                        key,
                        value: this.formatCurrencyWithCode(v, this.currency)
                    };
                } else {
                    sums[key] = {
                        key,
                        value: "N/A"
                    };
                }
            });

            return sums;
        }
    }
};
</script>
